import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

export interface AppSelectOption {
  value: string | number;
  label: string;
}

export interface AppSelectGroup {
  label: string;
  options: AppSelectOption[];
}

export interface AppSelectTreeNode {
  value: string | number;
  label: string;
  children?: AppSelectTreeNode[];
}

interface AppSelectProps {
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
  multiple?: boolean;
  values?: (string | number)[];
  onChangeMultiple?: (values: (string | number)[]) => void;
  options?: AppSelectOption[];
  groups?: AppSelectGroup[];
  nodes?: AppSelectTreeNode[];      // tree mode
  placeholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  style?: React.CSSProperties;
}

const ACCENT = 'rgba(37,99,235,';

/* Find label by value in a tree */
function findTreeLabel(nodes: AppSelectTreeNode[], val: string | number | null | undefined): string | null {
  if (val == null) return null;
  for (const n of nodes) {
    if (String(n.value) === String(val)) return n.label;
    if (n.children) {
      const found = findTreeLabel(n.children, val);
      if (found) return found;
    }
  }
  return null;
}

const AppSelect: React.FC<AppSelectProps> = ({
  value,
  onChange,
  multiple = false,
  values,
  onChangeMultiple,
  options = [],
  groups,
  nodes,
  placeholder = '— Select —',
  searchable = false,
  disabled = false,
  size = 'md',
  className = '',
  style,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hovered, setHovered] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const height = size === 'sm' ? 28 : 32;
  const fontSize = size === 'sm' ? 11 : 12;
  const radius = size === 'sm' ? 6 : 8;

  // Resolve selected label
  const allFlatOptions = groups ? groups.flatMap(g => g.options) : options;
  const selectedFlat = allFlatOptions.find(o => String(o.value) === String(value));
  const selectedLabel = nodes
    ? findTreeLabel(nodes, value)
    : selectedFlat?.label ?? null;

  const multiFirstLabel = multiple && (values?.length ?? 0) > 0
    ? allFlatOptions.find(o => String(o.value) === String(values![0]))?.label ?? String(values![0])
    : null;

  const displayLabel = multiple
    ? (values?.length ?? 0) > 0
      ? multiFirstLabel
      : null
    : selectedLabel;

  const open = useCallback(() => {
    if (disabled) return;
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
    setIsOpen(true);
    setSearch('');
  }, [disabled]);

  const close = useCallback(() => { setIsOpen(false); setSearch(''); }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (!triggerRef.current?.contains(e.target as Node) && !dropRef.current?.contains(e.target as Node))
        close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [isOpen, close]);

  const pick = useCallback((val: string | number) => {
    onChange?.(val);
    close();
  }, [onChange, close]);

  const pickMulti = useCallback((val: string | number) => {
    if (!onChangeMultiple) return;
    const strVal = String(val);
    const current = values ?? [];
    const isSelected = current.some(v => String(v) === strVal);
    onChangeMultiple(isSelected ? current.filter(v => String(v) !== strVal) : [...current, val]);
  }, [onChangeMultiple, values]);

  const clearValue = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      onChangeMultiple?.([]);
    } else {
      onChange?.(null);
    }
  }, [onChange, onChangeMultiple, multiple]);

  const toggleExpand = useCallback((id: string | number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  /* ── Flat list rendering ── */
  const filter = (opts: AppSelectOption[]) =>
    search ? opts.filter(o => o.label.toLowerCase().includes(search.toLowerCase())) : opts;

  const renderItems = (opts: AppSelectOption[]) =>
    filter(opts).map(opt => {
      const isSel = multiple
        ? (values ?? []).some(v => String(v) === String(opt.value))
        : String(opt.value) === String(value);
      return (
        <div
          key={opt.value}
          onMouseDown={() => multiple ? pickMulti(opt.value) : pick(opt.value)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '7px 9px', borderRadius: 7, cursor: 'pointer',
            fontSize: 12, fontWeight: isSel ? 700 : 500,
            color: isSel ? 'var(--text-color)' : 'var(--text-color-calendar)',
            background: isSel ? `${ACCENT}.09)` : 'transparent',
            transition: 'background .1s',
          }}
          onMouseEnter={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
          onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          <span style={{ fontSize: 11, color: '#059669', width: 12, flexShrink: 0 }}>{isSel ? '✓' : ''}</span>
          {opt.label}
        </div>
      );
    });

  /* ── Tree rendering ── */
  const renderTree = (list: AppSelectTreeNode[], depth = 0): React.ReactNode =>
    list.map(node => {
      const hasChildren = !!node.children?.length;
      const isExpanded = expandedIds.has(node.value);
      const isSel = String(node.value) === String(value);

      return (
        <div key={node.value}>
          <div
            onMouseDown={() => pick(node.value)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 9px', paddingLeft: 9 + depth * 14,
              borderRadius: 7, cursor: 'pointer', position: 'relative',
              fontSize: 12, fontWeight: hasChildren ? 700 : (isSel ? 700 : 500),
              color: isSel ? 'var(--text-color)' : hasChildren ? 'var(--text-color)' : 'var(--text-color-calendar)',
              background: isSel ? `${ACCENT}.09)` : 'transparent',
              transition: 'background .1s',
            }}
            onMouseEnter={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
            onMouseLeave={e => { if (!isSel) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
          >
            {isSel && <div style={{ position: 'absolute', left: 0, top: '15%', height: '70%', width: 2, background: '#2563eb', borderRadius: '0 1px 1px 0' }} />}
            <span style={{ flex: 1, lineHeight: 1.3 }}>{node.label}</span>
            {!hasChildren && isSel && <span style={{ fontSize: 11, color: '#059669', flexShrink: 0 }}>✓</span>}
            {hasChildren && (
              <span
                onMouseDown={e => toggleExpand(node.value, e)}
                style={{
                  fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0,
                  transition: 'transform .2s', transform: isExpanded ? 'rotate(90deg)' : 'none',
                  lineHeight: 1, padding: '2px 3px',
                }}
              >›</span>
            )}
          </div>
          {hasChildren && isExpanded && (
            <div>{renderTree(node.children!, depth + 1)}</div>
          )}
        </div>
      );
    });

  const borderColor = isOpen
    ? `${ACCENT}.45)`
    : hovered && !disabled ? `${ACCENT}.3)` : 'var(--border-input)';

  return (
    <div className={className} style={{ position: 'relative', ...style }}>
      <div
        ref={triggerRef}
        onClick={() => isOpen ? close() : open()}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          height, display: 'flex', alignItems: 'center', gap: 6,
          background: 'var(--bg-input)', color: 'var(--text-color)',
          border: `1px solid ${borderColor}`,
          borderRadius: radius, fontSize, fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          overflow: 'hidden', padding: '0 8px',
          userSelect: 'none', opacity: disabled ? 0.5 : 1,
          transition: 'border-color .15s',
          boxShadow: isOpen ? `0 0 0 3px ${ACCENT}.07)` : 'none',
        }}
      >
        <span style={{
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontWeight: displayLabel ? 600 : 400,
          color: displayLabel ? 'var(--text-color)' : 'var(--text-color-calendar-week)',
        }}>
          {displayLabel ?? placeholder}
        </span>
        {multiple && (values?.length ?? 0) > 1 && (
          <span style={{
            background: '#2563eb', color: '#fff', borderRadius: 9,
            fontSize: 10, fontWeight: 700, padding: '0 5px', minWidth: 17,
            textAlign: 'center', lineHeight: '17px', flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>+{values!.length}</span>
        )}
        {displayLabel && (multiple ? !!onChangeMultiple : !!onChange) && (
          <span
            onClick={clearValue}
            style={{ fontSize: 11, color: 'var(--text-color-calendar-week)', flexShrink: 0, lineHeight: 1, padding: '0 2px' }}
          >✕</span>
        )}
        {!(multiple && (values?.length ?? 0) > 0) && (
          <span style={{
            fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0,
            transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none', lineHeight: 1,
          }}>▾</span>
        )}
      </div>

      {isOpen && ReactDOM.createPortal(
        <div
          ref={dropRef}
          data-appselect-portal="true"
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: dropPos.top, left: dropPos.left, width: dropPos.width,
            zIndex: 99999, background: 'var(--bg-form)',
            border: '1px solid var(--border-input)',
            borderRadius: 12, boxShadow: '0 10px 40px rgba(0,0,0,.2)',
            overflow: 'hidden',
          }}
        >
          {nodes ? (
            <div style={{ overflowY: 'auto', maxHeight: 260, padding: 5, scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' } as React.CSSProperties}>
              {renderTree(nodes)}
            </div>
          ) : multiple ? (
            /* Multi-select: pinned selected items float to top + divider + searchable unselected below */
            (() => {
              const flatOpts = groups ? groups.flatMap(g => g.options) : options;
              const selectedOpts = (values ?? [])
                .map(v => flatOpts.find(o => String(o.value) === String(v)))
                .filter(Boolean) as AppSelectOption[];
              const unselectedOpts = flatOpts.filter(o => !(values ?? []).some(v => String(v) === String(o.value)));
              const filteredUnselected = search
                ? unselectedOpts.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
                : unselectedOpts;
              return (
                <>
                  {selectedOpts.length > 0 && (
                    <div style={{ padding: '5px 5px 0' }}>
                      {selectedOpts.map(opt => (
                        <div key={opt.value} onMouseDown={() => pickMulti(opt.value)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--text-color)', background: `${ACCENT}.09)`, transition: 'background .1s' }}
                          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${ACCENT}.14)`; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${ACCENT}.09)`; }}
                        >
                          <span style={{ fontSize: 11, color: '#059669', width: 12, flexShrink: 0 }}>✓</span>
                          {opt.label}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedOpts.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px 0' }}>
                      <div style={{ flex: 1, height: 1, background: 'var(--border-input)' }} />
                      <span style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', fontWeight: 600 }}>
                        {selectedOpts.length} selected
                      </span>
                      <div style={{ flex: 1, height: 1, background: 'var(--border-input)' }} />
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px 4px' }}>
                    <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>⌕</span>
                    <input
                      autoFocus={selectedOpts.length === 0}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search…"
                      style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, fontFamily: 'inherit', color: 'var(--text-color)', background: 'transparent', minWidth: 0 }}
                    />
                  </div>
                  <div style={{ overflowY: 'auto', maxHeight: 220, padding: '0 5px 5px', scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' } as React.CSSProperties}>
                    {filteredUnselected.map(opt => (
                      <div key={opt.value} onMouseDown={() => pickMulti(opt.value)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--text-color-calendar)', background: 'transparent', transition: 'background .1s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--btn-user-form)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      >
                        <span style={{ fontSize: 11, color: '#059669', width: 12, flexShrink: 0 }} />
                        {opt.label}
                      </div>
                    ))}
                    {filteredUnselected.length === 0 && search && (
                      <div style={{ padding: '10px 9px', fontSize: 12, color: 'var(--text-color-calendar-week)', textAlign: 'center' }}>No results</div>
                    )}
                  </div>
                </>
              );
            })()
          ) : (
            /* Single-select — original behaviour */
            <>
              {searchable && !nodes && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px 6px', borderBottom: '1px solid var(--border-input)' }}>
                  <span style={{ fontSize: 12, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>⌕</span>
                  <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 12, fontFamily: 'inherit', color: 'var(--text-color)', background: 'transparent', minWidth: 0 }} />
                </div>
              )}
              <div style={{ overflowY: 'auto', maxHeight: 260, padding: 5, scrollbarWidth: 'thin', scrollbarColor: 'var(--border-input) transparent' } as React.CSSProperties}>
                {groups
                  ? groups.map((group, gi) => (
                      <div key={gi}>
                        {gi > 0 && <div style={{ height: 1, background: 'var(--border-input)', margin: '4px 5px' }} />}
                        <div style={{ padding: '5px 9px 2px', fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>
                          {group.label}
                        </div>
                        {renderItems(group.options)}
                      </div>
                    ))
                  : renderItems(options)}
              </div>
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default AppSelect;
